import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Text,
  VisuallyHidden,
  useMediaQuery,
} from '@everlywell/ui-kit';
import { MagnifyingGlass, X, Sparkle } from '@phosphor-icons/react';
import {
  displayName,
  findTestByProductSlug,
} from 'components/Everywhere/helpers';
import { useEverywherePricing } from 'components/Everywhere/useEverywherePricing';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { trackEva } from 'utils/eva';
import { useProductSwapExperiments } from 'utils/hooks';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import { useAvailableEverywhereProductSlugs } from '../hooks/useAvailableEverywhereProductSlugs';
import type { SearchProductItem } from '../hooks/useEvaTraditionalSearch';
import { useEvaTraditionalSearch } from '../hooks/useEvaTraditionalSearch';
import { useModalShell } from '../hooks/useModalShell';
import { useProducts } from '../hooks/useProducts';
import { useRenderGuard } from '../hooks/useRenderGuard';
import EvaTopicSuggestions from './EvaTopicSuggestions';
import EvaTrustBar from './EvaTrustBar';
import EvaProductCard from './products/EvaProductCard';
import Everlywell360ProductCard from './products/Everlywell360ProductCard';
import EverywhereSearchProductCard, {
  assignEverywhereLabVisitImages,
} from './products/EverywhereSearchProductCard';

const DESKTOP_PAGE_SIZE = 6;
const MOBILE_PAGE_SIZE = 4;

const is360Product = (slug: string): boolean =>
  slug.includes('360-full-body-test');

const getCardRevealSx = (index: number) => ({
  opacity: 0,
  animation: `searchCardReveal 0.4s ease-out forwards`,
  animationDelay: `${60 + index * 80}ms`,
  '@keyframes searchCardReveal': {
    '0%': { opacity: 0, transform: 'translateY(12px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
});

type EvaTraditionalSearchModalProps = {
  isOpen?: boolean;
  onClose: () => void;
  hasAIMode?: boolean;
  onSwitchToAI?: (pendingText?: string) => void;
  initialQuery?: string;
  location?: string;
};

function SearchResultsGrid({
  searchResults,
  onClose,
}: {
  searchResults: SearchProductItem[];
  onClose: () => void;
}): JSX.Element | null {
  const productsBySlug = useProducts();
  const { labPriceFor } = useEverywherePricing();
  const { advertisedPromos } = useAdvertisedPromos();
  const [page, setPage] = useState(0);
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const { applyToProductList, applyToDisplayName } = useProductSwapExperiments(
    searchResults.map((item) => item.slug),
  );

  const displayableResults = useMemo(
    () =>
      // Apply product swap experiments (filter + reorder) first, then drop
      // anything unbuyable so customers never see an out-of-stock result.
      applyToProductList(searchResults, (item) => item.slug).filter((item) => {
        if (item.kind === 'everywhere') {
          return !!findTestByProductSlug(item.slug);
        }
        if (is360Product(item.slug)) return true;
        const product = productsBySlug[item.slug];
        return !!product && !product.outOfStock;
      }),
    [searchResults, productsBySlug, applyToProductList],
  );
  const displayCount = displayableResults.length;
  const totalPages = Math.ceil(displayCount / pageSize);
  const pagedResults = displayableResults.slice(
    page * pageSize,
    (page + 1) * pageSize,
  );
  const everywhereImageIndexes = assignEverywhereLabVisitImages(
    pagedResults.map((item) => {
      if (item.kind !== 'everywhere') return null;
      return findTestByProductSlug(item.slug)?.name ?? null;
    }),
    page,
  );

  const lastTrackedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (displayCount === 0) return;
    const key = searchResults.map((r) => r.slug).join(',');
    if (lastTrackedQueryRef.current === key) return;
    lastTrackedQueryRef.current = key;

    const products = displayableResults.map((item) => {
      if (item.kind === 'everywhere') {
        const test = findTestByProductSlug(item.slug);
        return {
          slug: item.slug,
          ...(test
            ? {
                name: displayName(test),
                price: labPriceFor(test) ?? undefined,
                product_type: 'everywhere',
              }
            : {}),
        };
      }

      const product = productsBySlug[item.slug];
      return {
        slug: item.slug,
        ...(product
          ? {
              id: product.productId,
              variant_id: product.variantId,
              name: product.displayName,
              price: product.price,
            }
          : {}),
      };
    });
    trackEva('Eva Search Recommended Products', {
      products,
      experience: 'search',
      result_count: displayCount,
    });
  }, [
    displayCount,
    displayableResults,
    labPriceFor,
    productsBySlug,
    searchResults,
  ]);

  if (displayCount === 0) return null;

  return (
    <Box width="100%">
      <Text
        fontFamily="DM Sans"
        fontSize="sm"
        color="tints.darkGrey"
        marginBottom={4}
      >
        {displayCount} result{displayCount !== 1 ? 's' : ''} found
      </Text>

      <Box
        display="grid"
        gridTemplateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)']}
        gap={[3, 4]}
        width="100%"
        sx={{
          '& > *': { minWidth: 0 },
          gridAutoRows: ['auto', '1fr'],
        }}
      >
        {pagedResults.map((item, idx) => {
          if (item.kind === 'everywhere') {
            const test = findTestByProductSlug(item.slug);
            if (!test) return null;

            return (
              <Box key={item.slug} height="100%" sx={getCardRevealSx(idx)}>
                <EverywhereSearchProductCard
                  test={test}
                  description={item.description}
                  onCardClick={onClose}
                  imageIndex={everywhereImageIndexes[idx]}
                />
              </Box>
            );
          }

          if (is360Product(item.slug)) {
            return (
              <Box
                key={item.slug}
                height="100%"
                maxWidth="380px"
                sx={getCardRevealSx(idx)}
              >
                <Everlywell360ProductCard
                  description={item.description}
                  advertisedPromos={advertisedPromos}
                />
              </Box>
            );
          }

          const product = productsBySlug[item.slug];
          if (!product) return null;

          return (
            <Box key={item.slug} height="100%" sx={getCardRevealSx(idx)}>
              <EvaProductCard
                product={{
                  ...product,
                  displayName: applyToDisplayName(
                    product.slug,
                    product.displayName,
                  ),
                }}
                advertisedPromos={advertisedPromos}
                description={item.description}
                experience="search"
                onCardClick={onClose}
              />
            </Box>
          );
        })}
      </Box>

      {totalPages > 1 && (
        <Flex justifyContent="center" gap={2} marginTop={8}>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              type="button"
              size="sm"
              variant={i === page ? 'solid' : 'ghost'}
              onClick={() => setPage(i)}
              minWidth="36px"
              height="36px"
              borderRadius="full"
              backgroundColor={i === page ? 'eggplant.base' : 'transparent'}
              color={i === page ? 'tints.white' : 'eggplant.base'}
              _hover={{
                backgroundColor:
                  i === page ? 'eggplant.base' : 'tints.lightCream',
              }}
              fontFamily="DM Sans"
              fontSize="sm"
            >
              {i + 1}
            </Button>
          ))}
        </Flex>
      )}
    </Box>
  );
}

function SearchBarForm({
  inputValue,
  isConnecting,
  isSearching,
  hasAIMode,
  onInputChange,
  onSubmit,
  onSwitchToAI,
}: {
  inputValue: string;
  isConnecting: boolean;
  isSearching: boolean;
  hasAIMode: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchToAI?: (pendingText?: string) => void;
}): JSX.Element {
  return (
    <Flex
      as="form"
      onSubmit={onSubmit}
      width="100%"
      maxWidth="680px"
      alignItems="center"
      paddingLeft="20px"
      paddingRight="8px"
      paddingY="8px"
      borderRadius="20px"
      backgroundColor="tints.white"
      boxShadow="0 4px 35px rgba(0, 0, 0, 0.08)"
      gap="8px"
    >
      <Icon
        as={MagnifyingGlass}
        boxSize="20px"
        color="tints.darkGrey"
        flexShrink={0}
      />
      <VisuallyHidden as="label" htmlFor="eva-search-input">
        Search
      </VisuallyHidden>
      <Input
        id="eva-search-input"
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        placeholder="Search tests and products..."
        flex={1}
        border="none"
        outline="none"
        boxShadow="none"
        padding="0"
        margin="0"
        backgroundColor="transparent"
        fontFamily="DM Sans"
        fontSize="1rem"
        fontWeight="400"
        lineHeight="140%"
        color="tints.black"
        height="auto"
        autoComplete="off"
        autoFocus
        sx={{
          fontFeatureSettings: "'liga' off, 'clig' off",
          height: 'auto !important',
        }}
      />

      {hasAIMode && onSwitchToAI && (
        <Button
          type="button"
          onClick={() => {
            trackEva('Clicked AI Mode', {
              has_pending_query: inputValue.trim().length > 0,
              pending_query: inputValue.trim() || undefined,
            });
            onSwitchToAI(inputValue);
          }}
          display="flex"
          height="36px"
          paddingX="14px"
          alignItems="center"
          gap="6px"
          borderRadius="18px"
          backgroundColor="eggplant.wash"
          color="eggplant.base"
          fontFamily="DM Sans"
          fontSize="sm"
          fontWeight="medium"
          _hover={{ backgroundColor: 'rgba(116, 41, 116, 0.10)' }}
          flexShrink={0}
          whiteSpace="nowrap"
        >
          <Icon as={Sparkle} boxSize="16px" weight="fill" />
          AI Mode
        </Button>
      )}

      <Button
        type="submit"
        variant="ghost"
        display="flex"
        width="36px"
        height="36px"
        minWidth="36px"
        minHeight="36px"
        padding="8px"
        justifyContent="center"
        alignItems="center"
        borderRadius="12px"
        backgroundColor="eggplant.base"
        _disabled={{ backgroundColor: 'tints.lightGrey' }}
        aria-label="Search"
        isDisabled={isSearching || inputValue.trim().length === 0}
        flexShrink={0}
      >
        <Icon
          as={MagnifyingGlass}
          aria-hidden="true"
          color={
            isSearching || inputValue.trim().length === 0
              ? 'tints.uiGrey'
              : 'tints.white'
          }
          boxSize="16px"
          weight="bold"
        />
      </Button>
    </Flex>
  );
}

function SearchLoadingIndicator(): JSX.Element {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      paddingY={16}
      flexDirection="column"
      gap={4}
    >
      <Box
        width="40px"
        height="40px"
        borderRadius="full"
        border="3px solid"
        borderColor="eggplant.wash"
        borderTopColor="eggplant.base"
        sx={{
          animation: 'spin 0.8s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      <Text fontFamily="DM Sans" color="tints.darkGrey" fontSize="md">
        Searching...
      </Text>
    </Flex>
  );
}

function SearchWelcomeScreen({
  inputValue,
  isConnecting,
  isSearching,
  hasAIMode,
  onInputChange,
  onSubmit,
  onSwitchToAI,
  onTopicSelect,
}: {
  inputValue: string;
  isConnecting: boolean;
  isSearching: boolean;
  hasAIMode: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchToAI?: (pendingText?: string) => void;
  onTopicSelect: (label: string) => void;
}): JSX.Element {
  return (
    <Flex
      minHeight="100svh"
      width="100%"
      paddingX={[4, 32, 48]}
      paddingTop={['56px', 32]}
      paddingBottom={[4, 32]}
      flexDirection="column"
      alignItems="center"
      justifyContent={['flex-start', 'center']}
      textAlign="center"
      gap={0}
    >
      <Box width="100%" display="flex" justifyContent="center">
        <SearchBarForm
          inputValue={inputValue}
          isConnecting={isConnecting}
          isSearching={isSearching}
          hasAIMode={hasAIMode}
          onInputChange={onInputChange}
          onSubmit={onSubmit}
          onSwitchToAI={onSwitchToAI}
        />
      </Box>

      <Box width="100%" display="flex" justifyContent="center" marginTop="16px">
        <EvaTopicSuggestions onSelect={onTopicSelect} />
      </Box>

      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        marginTop={['32px', '80px']}
      >
        <EvaTrustBar />
      </Box>
    </Flex>
  );
}

export default function EvaTraditionalSearchModal({
  isOpen = true,
  onClose,
  hasAIMode = false,
  onSwitchToAI,
  initialQuery,
  location,
}: EvaTraditionalSearchModalProps): JSX.Element | null {
  const shouldRender = useRenderGuard(isOpen);
  useModalShell({ onClose });

  const [inputValue, setInputValue] = useState(initialQuery ?? '');
  const availableEverywhereProductSlugs = useAvailableEverywhereProductSlugs();

  const { query, results, isSearching, isConnecting, error, submitQuery } =
    useEvaTraditionalSearch(availableEverywhereProductSlugs);

  const initialQuerySentRef = React.useRef(false);

  React.useEffect(() => {
    if (
      initialQuery &&
      initialQuery.trim() &&
      !initialQuerySentRef.current &&
      shouldRender
    ) {
      initialQuerySentRef.current = true;
      trackEva('Sent Eva Traditional Search Query', {
        query: initialQuery.trim(),
        experience: 'search',
        ...(location ? { location } : {}),
      });
      void submitQuery(initialQuery.trim());
    }
  }, [initialQuery, shouldRender, submitQuery, location]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed) return;

      trackEva('Sent Eva Traditional Search Query', {
        query: trimmed,
        experience: 'search',
        ...(location ? { location } : {}),
      });
      void submitQuery(trimmed);
    },
    [inputValue, submitQuery, location],
  );

  const handleTopicSelect = useCallback(
    (label: string) => {
      setInputValue(label);
      trackEva('Sent Eva Traditional Search Query', {
        query: label,
        experience: 'search',
        source: 'suggestion_chip',
        ...(location ? { location } : {}),
      });
      void submitQuery(label);
    },
    [submitQuery, location],
  );

  if (!shouldRender) return null;

  const hasSearched = !!query || isSearching || !!error;
  const hasResults = results.length > 0;
  const showNoResults = !isSearching && !hasResults && !!query && !error;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={2000}
      height="calc(100vh-32px)"
      width="100vw"
      background="eggplant.wash"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      alignItems="center"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <Box
        position="fixed"
        top="0 !important"
        zIndex={1}
        backgroundColor="eggplant.wash"
        height="32px"
        width="100%"
      />

      <Button
        type="button"
        onClick={onClose}
        variant="ghost"
        position="absolute"
        top={['0.625rem', '1.25rem']}
        right={['0.625rem', '1.25rem']}
        width="2rem"
        height="2rem"
        minWidth="2rem"
        minHeight="2rem"
        padding="0.5rem"
        borderRadius="1.26875rem"
        backgroundColor="tints.white"
        color="eggplant.base"
        zIndex={2001}
        _hover={{ filter: 'brightness(0.97)' }}
        _active={{ filter: 'brightness(0.94)' }}
        aria-label="Close Search"
      >
        <Icon as={X} aria-hidden="true" color="eggplant.base" boxSize={16} />
      </Button>

      {!hasSearched ? (
        <SearchWelcomeScreen
          inputValue={inputValue}
          isConnecting={isConnecting}
          isSearching={isSearching}
          hasAIMode={hasAIMode}
          onInputChange={setInputValue}
          onSubmit={handleSubmit}
          onSwitchToAI={onSwitchToAI}
          onTopicSelect={handleTopicSelect}
        />
      ) : (
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
          maxWidth="1200px"
          paddingX={[4, 6]}
          paddingTop={['56px', '100px']}
          paddingBottom={6}
          flex={1}
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: '#E8E8E2',
              borderRadius: '2px',
            },
          }}
        >
          <Box width="100%" maxWidth="680px" marginBottom={6} flexShrink={0}>
            <SearchBarForm
              inputValue={inputValue}
              isConnecting={isConnecting}
              isSearching={isSearching}
              hasAIMode={hasAIMode}
              onInputChange={setInputValue}
              onSubmit={handleSubmit}
              onSwitchToAI={onSwitchToAI}
            />
          </Box>

          {isSearching && <SearchLoadingIndicator />}

          {error && (
            <Flex
              justifyContent="center"
              alignItems="center"
              paddingY={8}
              flexDirection="column"
              gap={3}
            >
              <Text
                fontFamily="DM Sans"
                color="red.500"
                fontSize="md"
                textAlign="center"
              >
                {error}
              </Text>
            </Flex>
          )}

          {showNoResults && (
            <Flex
              justifyContent="center"
              alignItems="center"
              paddingY={8}
              flexDirection="column"
              gap={3}
            >
              <Text
                fontFamily="DM Sans"
                color="tints.darkGrey"
                fontSize="lg"
                textAlign="center"
              >
                No results found for &ldquo;{query}&rdquo;
              </Text>
              <Text
                fontFamily="DM Sans"
                color="tints.uiGrey"
                fontSize="md"
                textAlign="center"
              >
                Try a different search term
              </Text>
            </Flex>
          )}

          {hasResults && (
            <SearchResultsGrid searchResults={results} onClose={onClose} />
          )}
        </Flex>
      )}
    </Box>
  );
}
